import { useState, useEffect, useCallback } from "react";
import { Plus, Save, X, Loader2, Eye, PenLine, Trash2, FilePlus2, Search, User } from "lucide-react";
import toast from "react-hot-toast";
import { getUserPermissions, createUserPermission, updateUserPermission, getMasterDropdownService } from "@/services/masters";
import { ScreenCombobox } from "@/components/ui";
import Pagination from "../common/Pagination";

interface UserPerm {
  id: number;
  Screen_Name: string;
  Can_View: boolean;
  Can_Create: boolean;
  Can_Update: boolean;
  Can_Delete: boolean;
  user: number;
  Created_At?: string;
  Updates?: any[];
}

interface EmployeeOption {
  id: number;
  label: string;
}

const PERM_COLS: {
  key: keyof Pick<UserPerm, "Can_View" | "Can_Create" | "Can_Update" | "Can_Delete">;
  label: string;
  icon: React.ReactNode;
  activeClass: string;
  checkClass: string;
}[] = [
  { key: "Can_View",   label: "View",   icon: <Eye className="w-3.5 h-3.5" />,      activeClass: "bg-brand-100 text-brand-700 border-brand-300",   checkClass: "accent-brand-500"   },
  { key: "Can_Create", label: "Create", icon: <FilePlus2 className="w-3.5 h-3.5" />, activeClass: "bg-green-100 text-green-700 border-green-300", checkClass: "accent-green-600" },
  { key: "Can_Update", label: "Update", icon: <PenLine className="w-3.5 h-3.5" />,   activeClass: "bg-amber-100 text-amber-700 border-amber-300", checkClass: "accent-amber-500"  },
  { key: "Can_Delete", label: "Delete", icon: <Trash2 className="w-3.5 h-3.5" />,    activeClass: "bg-red-100 text-red-700 border-red-300",      checkClass: "accent-red-600"   },
];

function PermBadge({ active, label, icon }: { active: boolean; label: string; icon: React.ReactNode }) {
  const col = PERM_COLS.find((c) => c.label === label)!;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${
      active ? col.activeClass : "bg-gray-100 text-gray-400 border-gray-200"
    }`}>
      {icon}{label}
    </span>
  );
}

export default function UserPermissions() {
  const [permissions, setPermissions] = useState<UserPerm[]>([]);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [screenOptions, setScreenOptions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchUser, setSearchUser] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Inline edit state
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<Record<string, boolean>>({});
  const [savingId, setSavingId] = useState<number | null>(null);

  // Add modal state
  const [showModal, setShowModal] = useState(false);
  const [newPerm, setNewPerm] = useState({
    Screen_Name: "",
    user: "" as string | number,
    Can_View: true,
    Can_Create: false,
    Can_Update: false,
    Can_Delete: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadData = useCallback(async (p = 1) => {
    setIsLoading(true);
    try {
      const [permRes, empRes] = await Promise.all([
        getUserPermissions(undefined, p),
        getMasterDropdownService(),
      ]);
      if (permRes.status === 200 && permRes.data) {
        const d = permRes.data as any;
        setPermissions(d.results ?? (Array.isArray(d) ? d : []));
        setTotalPages(d.total_pages ?? 1);
        setTotalCount(d.count ?? 0);
      }
      if (empRes.status === 200 && empRes.data) {
        const d = empRes.data as any;
        const items: any[] = d.Employees ?? (Array.isArray(d) ? d : []);
        setEmployees(items.map((e: any) => ({
          id: e.id,
          label: `${e.Emp_Name ?? e.Username}`,
        })));
        setScreenOptions((d.Screens as string[]) ?? []);
      }
    } catch {
      toast.error("Error loading user permissions");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadData(1); }, [loadData]);

  const handlePageChange = (p: number) => { setPage(p); loadData(p); };

  const getUserLabel = (userId: number) =>
    employees.find((e) => e.id === userId)?.label ?? `user #${userId}`;

  // Group by user
  const filtered = searchUser
    ? permissions.filter((p) => getUserLabel(p.user).toLowerCase().includes(searchUser.toLowerCase()) || String(p.user) === searchUser)
    : permissions;

  const grouped = filtered.reduce((acc, p) => {
    const key = String(p.user);
    (acc[key] ??= []).push(p);
    return acc;
  }, {} as Record<string, UserPerm[]>);

  // ── Inline edit ──────────────────────────────────────────────
  const startEdit = (p: UserPerm) => {
    setEditingId(p.id);
    setEditDraft({ Can_View: p.Can_View, Can_Create: p.Can_Create, Can_Update: p.Can_Update, Can_Delete: p.Can_Delete });
  };

  const cancelEdit = () => { setEditingId(null); setEditDraft({}); };

  const closeModal = () => {
    setShowModal(false);
    setNewPerm({ Screen_Name: "", user: "", Can_View: true, Can_Create: false, Can_Update: false, Can_Delete: false });
  };

  const saveEdit = async (id: number) => {
    setSavingId(id);
    try {
      const res = await updateUserPermission(id, {
        Can_View:   editDraft.Can_View,
        Can_Create: editDraft.Can_Create,
        Can_Update: editDraft.Can_Update,
        Can_Delete: editDraft.Can_Delete,
      });
      if (res.status === 200) {
        toast.success("Permission updated");
        setEditingId(null);
        await loadData(page);
      } else {
        toast.error("Failed to update");
      }
    } catch {
      toast.error("Error saving permission");
    } finally {
      setSavingId(null);
    }
  };

  // ── Add new ──────────────────────────────────────────────────
  const submitNew = async () => {
    if (!newPerm.Screen_Name || !newPerm.user) {
      toast.error("Screen name and user are required");
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await createUserPermission({
        Screen_Name: newPerm.Screen_Name,
        User: Number(newPerm.user),
        Can_View:   newPerm.Can_View,
        Can_Create: newPerm.Can_Create,
        Can_Update: newPerm.Can_Update,
        Can_Delete: newPerm.Can_Delete,
      });
      if (res.status === 201 || res.status === 200) {
        toast.success("user permission created");
        setShowModal(false);
        setNewPerm({ Screen_Name: "", user: "", Can_View: true, Can_Create: false, Can_Update: false, Can_Delete: false });
        setPage(1);
        await loadData(1);
      } else {
        const msg = (res.errors as any)?.non_field_errors?.[0] ?? "Failed to create";
        toast.error(msg);
      }
    } catch {
      toast.error("Error creating permission");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 p-1">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 px-6 py-5 rounded-xl shadow-lg">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <User className="w-5 h-5 text-white/80" />
              <h2 className="text-2xl font-bold text-white">User Permissions</h2>
            </div>
            <p className="text-white/70 text-sm">Override permissions for specific users</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white font-semibold py-2.5 px-4 rounded-lg transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            <Plus className="w-4 h-4" /> Add user Permission
          </button>
        </div>

        {/* Search */}
        <div className="relative mt-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60" />
          <input
            type="text"
            placeholder="Search by user name or ID…"
            value={searchUser}
            onChange={(e) => setSearchUser(e.target.value)}
            className="w-full pl-9 pr-8 py-1.5 bg-white/15 text-white placeholder-white/50 border border-white/25 rounded-lg text-sm focus:outline-none focus:bg-white/25"
          />
          {searchUser && (
            <button onClick={() => setSearchUser("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

<Pagination
        currentPage={page}
        totalPages={totalPages}
        totalCount={totalCount}
        itemsPerPage={20}
        onPageChange={(p) => {
          handlePageChange(p)
        }}
      />
      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
        </div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <User className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="font-medium">No user permissions found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([userId, perms]) => (
            <div key={userId} className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
              {/* user header */}
              <div className="flex items-center gap-3 px-5 py-3 bg-gradient-to-r from-teal-50 to-cyan-50 border-b border-teal-100">
                <div className="w-8 h-8 rounded-lg bg-teal-600 flex items-center justify-center shadow-sm">
                  <User className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="font-bold text-gray-800 text-sm">{getUserLabel(Number(userId))}</p>
                  <p className="text-xs text-gray-500">{perms.length} screen override{perms.length !== 1 ? "s" : ""}</p>
                </div>
              </div>

              {/* Permissions table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50/50">
                      <th className="text-left px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide w-48">Screen</th>
                      {PERM_COLS.map((col) => (
                        <th key={col.key} className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          <div className="flex items-center justify-center gap-1">{col.icon}{col.label}</div>
                        </th>
                      ))}
                      <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {perms.map((perm) => {
                      const isEditing = editingId === perm.id;
                      const isSaving  = savingId  === perm.id;
                      return (
                        <tr key={perm.id} className={`transition-colors ${isEditing ? "bg-teal-50/60" : "hover:bg-gray-50/60"}`}>
                          <td className="px-5 py-3">
                            <span className="font-medium text-gray-800">{perm.Screen_Name}</span>
                          </td>
                          {PERM_COLS.map((col) => (
                            <td key={col.key} className="text-center px-4 py-3">
                              {isEditing ? (
                                <input
                                  type="checkbox"
                                  checked={!!editDraft[col.key]}
                                  onChange={(e) => setEditDraft((d) => ({ ...d, [col.key]: e.target.checked }))}
                                  className={`w-4 h-4 rounded cursor-pointer ${col.checkClass}`}
                                />
                              ) : (
                                <PermBadge active={perm[col.key]} label={col.label} icon={col.icon} />
                              )}
                            </td>
                          ))}
                          <td className="text-center px-4 py-3">
                            {isEditing ? (
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  onClick={() => saveEdit(perm.id)}
                                  disabled={isSaving}
                                  className="flex items-center gap-1 px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold rounded-lg transition-all disabled:opacity-60"
                                >
                                  {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                                  Save
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  disabled={isSaving}
                                  className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-semibold rounded-lg transition-all"
                                >
                                  <X className="w-3 h-3" /> Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => startEdit(perm)}
                                className="px-3 py-1.5 text-xs font-semibold text-teal-600 bg-teal-50 hover:bg-teal-100 rounded-lg transition-all"
                              >
                                Edit
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      

      {/* Add Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-gradient-to-r from-teal-600 to-cyan-600 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-white" />
                <h3 className="text-lg font-bold text-white">Add user Permission</h3>
              </div>
              <button onClick={closeModal} className="text-white/70 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* user select */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                  user <span className="text-red-500">*</span>
                </label>
                <select
                  value={newPerm.user}
                  onChange={(e) => setNewPerm((p) => ({ ...p, user: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 bg-gray-50"
                >
                  <option value="">Select user…</option>
                  {employees.map((e) => (
                    <option key={e.id} value={e.id}>{e.label}</option>
                  ))}
                </select>
              </div>

              {/* Screen name */}
              <ScreenCombobox
                value={newPerm.Screen_Name}
                onChange={(val) => setNewPerm((p) => ({ ...p, Screen_Name: val }))}
                screens={screenOptions}
                label="Screen Name"
                required
              />

              {/* Permissions */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">Permissions</label>
                <div className="grid grid-cols-2 gap-3">
                  {PERM_COLS.map((col) => (
                    <label
                      key={col.key}
                      className={`flex items-center gap-2.5 p-3 rounded-xl border-2 cursor-pointer transition-all select-none ${
                        newPerm[col.key]
                          ? col.activeClass + " border-current"
                          : "border-gray-100 bg-gray-50 text-gray-400 hover:border-gray-200"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={newPerm[col.key]}
                        onChange={(e) => setNewPerm((p) => ({ ...p, [col.key]: e.target.checked }))}
                        className={`w-4 h-4 rounded ${col.checkClass}`}
                      />
                      <span className="flex items-center gap-1.5 text-sm font-semibold">{col.icon}{col.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={closeModal}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-semibold rounded-xl hover:bg-gray-50 transition-all text-sm"
              >
                Cancel
              </button>
              <button
                onClick={submitNew}
                disabled={isSubmitting}
                className="flex-1 py-2.5 bg-gradient-to-r from-teal-600 to-cyan-600 text-white font-semibold rounded-xl hover:opacity-90 transition-all flex items-center justify-center gap-2 text-sm disabled:opacity-60"
              >
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {isSubmitting ? "Creating…" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
